import { useRouter } from 'vue-router/composables';
import { useQueryClient } from '@tanstack/vue-query';
import { createEventHook, noop } from '@vueuse/core';
import { readonly, ref } from 'vue';
import { VOICE_AI_AVAILABILITY_QUERY_KEY } from '@/components/chat/messages/ConnectOptionsMessage/useVoiceAiAvailability';

import { PleasedApi } from '@/pleased-sdk/PleasedApi';
import { i18n } from '@/plugins/i18n';
import { $pr } from '@/pleased-sdk/PleasedReporting';
import { switcher } from '@/common/switcher';
import * as Locale from '@/plugins/i18n';
import { useConfig } from '@/stores/useConfig';
import { useEventLogger } from '@/composables/useEventLogger/useEventLogger';
import { logger as globalLogger } from '@/common/api/logger';

import type {
  AlternativeActionSelectedEvent,
  ApplyMetadataAutomationExternEvent,
  ArticleInteractionExternEvent,
  ArticleListNavigationEvent,
  ArticleListWithOtherNavigationMessage,
  ArticleSystemOption,
  BaseEvent,
  BaseExternalEvent,
  BaseMessage,
  BaseSystemMessage,
  ConnectToAgent,
  MessageConfiguration,
  PredictiveSuggestionSelectionEvent,
  PreSupportSelectionEvent,
  ResolutionEvent,
  ResumeLiveChatExternEvent,
  RollbackEvent,
  SystemListMessage,
  SystemOption,
  SystemOptionEvent,
  UserSelectedTransactionListEvent,
  UserEditSelectedTransactionEvent,
  UserSelectedConnectOptionEvent,
  LowCsatConnectExternEvent,
} from '../message/types';
import type { MessageUtilities } from '../message/useMessageUtilities';
import {
  EventType,
  buildArticleOption,
  buildConnectConfig,
  buildConnectToAgentWithArticleConfig,
  buildResolution,
  canConnectToAgent,
  checkABTestingPassed,
  createArticleCheckFailureMessage,
  createMessageHistory,
  createSelectedTransactionMessage,
  createSystemMessage,
  createTransactionSelectMessage,
  createUserSearchArticleMessage,
  createUserSystemMessage,
  generateUserSideArticleMessageId,
  getCategoryTitleFromMessageHistory,
  getSectionTitleFromMessageHistory,
  isMessageUserSideArticle,
  randomId,
} from '../message/lib';
import {
  ArticleInteractionType,
  ConnectOptionKind,
  ConnectOptionType,
  ExternalBotEvent,
  MessageType,
  PredefinedOptionType,
  SystemOptionType,
} from '../message/enum';
import { isArticleOption, isCategoryOption, isSectionOption } from '../message/lib';
import type { PrePromptConditions } from '../message/lib';
import { conditionHelper } from '../message/lib';

import { ChatTriggerMatchSource } from '@/common/bot/flow-frameworks/chat-trigger';
import { useDialogTrigger } from '@/stores/useDialogTrigger';
import { useAiFlowEventHandlers } from './ai-service/useAiFlowEventHandlers';
import { platform } from '@/common/bridge';
import {
  getShouldUseNativeMicroAccessCheck,
  requestNativeAppVoiceMicrophonePermission,
  sendNativeAppVoiceMicrophoneSettings,
} from '@/library/pleased-chat-controller/voiceNativeMicroAccess';
import { useConnectConfig } from '@/stores/useConnectConfig';
import { storeToRefs } from 'pinia';
import { getPredictionById } from '@/common/bot/flow-frameworks/ChatMessagePredictiveSuggestions.static';
import { useBrandConfig } from '@/stores/useBrandConfig';
import { createConnectOptionSelectMessage, canCheckMetadataAutomation } from '../message/lib';
import { buildCategoryListMessageFromBrandConfig } from './categoryBrowsing.utils';
import * as ItemType from '@/common/bot/lib/ItemType';
import { ResolutionKind, ResolutionType } from '@/common/bot/enum/ResolutionType';
import { EVENT_TYPES, FEATURES_EVENTS } from '@/composables/useEventLogger';
import { CHECKS } from '@/common/bot/flow-frameworks/ChatMessageChecks.static';
import { checkActionSupported, resolveArticleAction } from '@/common/bot/flow-frameworks/ChatMessageActions.static';
import { useBotUser } from '@/stores/useBotUser';
import { useChatWidget, InteractionView } from '@/stores/useChatWidget';
import { getTransactionId, TRANSACTION_TYPE_NAME_ENUM } from '@/common/transaction-utils';
import { ExtendedSelectedTransStatement } from '@/common/api/models.pleased';
import { getConnectOptionChannelLabel } from '@/common/connectOption.utils';
import { getAiHardcodedConnectConfig } from './ai-service/useAiFlowEventHandlers.utils';
import { devLog } from '@/common/debug';
import { isLandingEntrySupported, resolveLandingEntryUserId } from '@/common/landingEntry.utils';
import { addBreadcrumb, captureEventException, ErrorTrackingEvent } from '@/common/error-tracking';
import {
  buildSkipToLiveAgentConnectConfig,
  buildSkipWelcomeSystemMessage,
  hasSkipWelcomeMessage,
} from '@/common/skipToLiveAgent.utils';

export function useFlowEventHandlers({ conversation }: { conversation: MessageUtilities }) {
  const { config } = useConfig();
  const logger = useEventLogger();
  const router = useRouter();

  const botUser = useBotUser();
  const { user, isAuthenticated } = storeToRefs(botUser);

  const getLandingEntryUserId = () => resolveLandingEntryUserId(isAuthenticated.value, user.value.id);

  const { setIsChatOverlayVisible, setInteractionView } = useChatWidget();
  const queryClient = useQueryClient();

  const { combineTriggerCheck } = useDialogTrigger();

  const aiHandlers = useAiFlowEventHandlers({
    conversation,
    connectToAgent,
    connectArticleWithAcknowledgement,
  });

  const connectToSupportHook = createEventHook<{
    connectToAgentConfig: ConnectToAgent;
  }>();

  const endChatHook = createEventHook();
  const restartChatHook = createEventHook();
  const abortCancelRequestHook = createEventHook();
  const transactionSelectedHook = createEventHook<UserSelectedTransactionListEvent['payload']>();
  const editSelectedTransactionHook = createEventHook<void>();

  const connectOptionSelectedHook = createEventHook<{ connectOption: ConnectOptionKind; confirmation?: boolean }>();
  const endLiveChatHook = createEventHook<{ resolution: ResolutionKind }>();
  const endVoiceCallHook = createEventHook<{ resolution: ResolutionKind }>();
  const resumeChatHook = createEventHook<ResumeLiveChatExternEvent['payload']>();
  const voiceAiUnavailableHook = createEventHook<void>();
  const landingAiVoiceStartHook = createEventHook<void>();

  const connectConfigStore = useConnectConfig();
  const { setConnectConfig, updateConnectConfig } = connectConfigStore;
  const { connectConfig } = storeToRefs(connectConfigStore);

  const brandConfigStore = useBrandConfig();
  const { brandConfig, isSkipToLiveAgentActive } = storeToRefs(brandConfigStore);

  const status = ref<'ongoing' | 'connect' | 'ended'>('ongoing');

  /**
   * When skip-to-live-agent is enabled, category/article browsing must not resume.
   * Starts the brand-level connect wizard (guest form or channel picker) instead.
   */
  const redirectBrowseToSkipFlowIfActive = (): boolean => {
    if (!isSkipToLiveAgentActive.value) {
      return false;
    }

    enterSkipToLiveAgentFlow();
    return true;
  };

  function onCategorySelected(event: SystemOptionEvent) {
    const {
      payload: { selected: selectedCategory },
      message,
    } = event;

    $pr.selectedCategory(selectedCategory.id);
    conversation.addItems({ ...message, selectedOption: selectedCategory } as SystemListMessage);

    const userSelectedCategoryMessage = createUserSystemMessage({
      sendToChat: true,
      message: i18n.t('chat.user.response.message.text', { name: selectedCategory.title }) as string,
      interactiveMessageParts: [
        i18n.t('chat.user.response.message.part', { name: selectedCategory.title }) as string,
        selectedCategory.title,
      ],
      history: createMessageHistory(event, message),
    });

    conversation.addItems(userSelectedCategoryMessage);

    // Get the category details
    const categoryMessage = PleasedApi.getCategoryDetails
      .cached({ categoryId: selectedCategory.id })
      .then(({ data: { id: sectionId, categoryConfig, sections, articles } }) => {
        // Here is an indirect dependency on the state of the chatbot, can be removed when refactoring.
        const articleOptions = articles.map(buildArticleOption);
        // .map(({ action, ...x }) => (action && checkActionSupported(action) ? { ...x, action: resolveArticleAction(action) } : x));
        const sectionOptions = sections.map((s) => ({ ...s, kind: SystemOptionType.SECTION }));

        // TODO: add category and section to connect config
        const connectConfig = buildConnectConfig(categoryConfig);
        setConnectConfig({
          ...connectConfig,
          category: selectedCategory.title,
        });

        // Fallback to old UI if there are no articles configured in the category.
        const config: MessageConfiguration = {
          // noneOfTheAbove: buildNoneOfTheAboveConfig(noneOfTheAboveConfig),
          connect: { ...buildConnectConfig(categoryConfig), category: selectedCategory.title },
          // resolution: buildResolution(categoryConfig),
        };

        const categoryPassAbTesting = checkABTestingPassed({
          userId: user.value.id,
          abValue: categoryConfig.abValue || '',
          enableAbTesting: categoryConfig.enableAbTesting || false,
          articleId: categoryConfig.id,
        });

        return !articleOptions.length
          ? createSystemMessage<SystemListMessage>({
              type: MessageType.SECTION_LIST,
              id: `sections-${sectionId}-${randomId()}`,
              options: sectionOptions,
              config,
              message: categoryConfig.responseMessage || '',
              history: createMessageHistory(event, message),
            })
          : createSystemMessage<ArticleListWithOtherNavigationMessage>({
              type: MessageType.ARTICLE_LIST_WITH_OTHER_NAVIGATION,
              id: `articles-or-sections-${sectionId}-${randomId()}`,
              options: articleOptions,
              config,
              sections: sectionOptions || [],
              elements: {
                browseArticles: Boolean(sectionOptions.length),
                issueNotListed: canConnectToAgent(config.connect) && categoryPassAbTesting,
              },
              message: categoryConfig.responseMessage || '',
              history: createMessageHistory(event, message),
            });
      });

    conversation.enqueue(
      combineTriggerCheck({
        into: categoryMessage,
        type: ChatTriggerMatchSource.CATEGORY,
        id: selectedCategory.id,
      })
    );
  }

  function onSectionSelected(event: SystemOptionEvent) {
    const {
      payload: { selected: section },
      message,
    } = event;

    $pr.selectedSection(section.id);
    conversation.addItems({ ...message, selectedOption: section } as SystemListMessage);

    const userMessage = createUserSystemMessage({
      message: i18n.t('chat.user.response.message.text', { name: section.title }) as string,
      interactiveMessageParts: [i18n.t('chat.user.response.message.part') as string, section.title],
      history: createMessageHistory(event, message),
    });
    conversation.addItems(userMessage);

    const isFirstLevelSection = message.config && !message.config.connect;

    const loadingSectionMessage = PleasedApi.getSectionDetails
      .cached({ sectionId: section.id })
      .then(({ data: { sectionConfig, sections, articles } }) => {
        // First level sections use their own config.
        const overrideParentConfig = sectionConfig.overrideParentConfig;
        const connectConfig = buildConnectConfig(sectionConfig);
        setConnectConfig({
          ...connectConfig,
          section: section.title,
          category: message.config?.connect?.category || '',
        });

        const config = overrideParentConfig
          ? {
              connect: buildConnectConfig(sectionConfig),
              resolution: buildResolution(sectionConfig),
            }
          : { ...message.config };
        const responseMessage = overrideParentConfig ? sectionConfig.responseMessage || '' : message.message;

        const mappedSections: SystemOption[] = (sections || []).map((s) => ({ ...s, kind: SystemOptionType.SECTION }));
        const mappedArticles: SystemOption[] = (mappedSections.length ? [] : articles).map((a) => ({
          ...a,
          kind: SystemOptionType.ARTICLE,
        }));

        const options = mappedSections.concat(mappedArticles);

        const messageToShow = createSystemMessage<SystemListMessage>({
          type: MessageType.HYBRID_LIST,
          config,
          options,
          message: responseMessage,
          history: createMessageHistory(event, message, userMessage),
        });
        return messageToShow;
      });

    conversation.enqueue(
      combineTriggerCheck({
        into: loadingSectionMessage,
        type: ChatTriggerMatchSource.SECTION,
        id: section.id,
      })
    );
  }

  // Now we have chatbot overlay available, so we pre-create "Back to chatroom" message
  function moveUserToArticlePage(article: ArticleSystemOption, sourceMessageId: string) {
    const parentWithArticleConfig = buildConnectToAgentWithArticleConfig(connectConfig.value!, article);

    onArticleBackToChatroom({
      type: ExternalBotEvent.ARTICLE_PAGE_INTERACTION,
      id: `ArticleInteraction-${Date.now()}`,
      payload: {
        interaction: ArticleInteractionType.BACK,
        supportOptions: parentWithArticleConfig,
        article,
      },
    });

    router.push({
      name: 'new-article',
      params: {
        id: `${article.id}`,
      },
      query: {
        chatWidget: 'true',
        sourceMessageId,
      },
    });
  }

  async function onArticleSelected(event: SystemOptionEvent | ArticleListNavigationEvent) {
    const {
      payload: { selected },
      message,
    } = event;

    if (typeof selected === 'string' || !('id' in selected)) {
      globalLogger.error('[CF] Article selected event does not have article id');
      return;
    }

    const option = selected as ArticleSystemOption;

    $pr.selectedArticle(option.id);
    const updatedMessage = { ...message, selectedOption: selected };
    conversation.addItems(updatedMessage as SystemListMessage);

    const parentWithArticleConfig = buildConnectToAgentWithArticleConfig(message?.config?.connect!, option);

    addArticleUserMessage(option, event);

    // If article is hidden and user has overridden parent config, connect directly
    if (
      canConnectToAgent(parentWithArticleConfig) &&
      option.overrideParentConnectAgent &&
      option.hideContent &&
      !option.checkActionId
    ) {
      const articleId = option.articleId;
      const fetchMetadata = parentWithArticleConfig.enableMetadataRules && !user.value.isGuest && articleId != null;
      // TODO: probably refactor metadata rules fetching to tanstack + fetch directly?
      const metadataRulesResult = await (fetchMetadata
        ? PleasedApi.getMetadataAutomations({ articleId }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }));
      const metadataRules = metadataRulesResult?.data || [];
      updateConnectConfig({ ...parentWithArticleConfig, metadataRules });
      connectToAgent();
      return;
    }

    // No checks necessary or check already failed, open the article page
    if (!option.checkActionId || ItemType.isArticleCheckFailureMessage(message)) {
      moveUserToArticlePage(option, message.id as string);
      return;
    }

    // If checks are present, get the check config and run the check
    const nextMessage = PleasedApi.getArticleDetails
      .cached({ articleId: option.checkActionId })
      .then(({ data: fetchedArticle }) => {
        const article = fetchedArticle;
        const category = fetchedArticle.category;

        const checkArticleOption = buildArticleOption({
          ...article.articleConfig,
          id: article.id,
          title: article.title,
          category: category?.titleTranslated || category?.title || '',
        });

        return runCheck({ check: checkArticleOption.checks || '' }).then((checkPassed) => ({
          checkPassed,
          checkArticleOption,
        }));
      })
      .then(({ checkPassed, checkArticleOption }) => {
        if (checkPassed) {
          // Check passed
          if (canConnectToAgent(parentWithArticleConfig) && option.hideContent) {
            updateConnectConfig(parentWithArticleConfig);
            connectToAgent();
          } else {
            moveUserToArticlePage(option, message.id as string);
          }

          // Return null - no message to queue
          return null;
        }

        // Check not passed, show the corresponding action
        const slots = CHECKS.KYC.slots();
        const articleAction = checkActionSupported(checkArticleOption.action)
          ? { action: resolveArticleAction(checkArticleOption.action!) }
          : {};

        return createArticleCheckFailureMessage({
          message: slots.message,
          article: { ...checkArticleOption, ...articleAction },
          messageSlots: {
            afterArticle: slots.actionMessage,
          },
          config: {
            connect: parentWithArticleConfig,
            ...articleAction,
          },
        });
      });

    conversation.enqueue(
      combineTriggerCheck({
        into: nextMessage,
        type: ChatTriggerMatchSource.ARTICLE,
        id: option.id,
      })
    );
  }

  async function runCheck({ check }: { check: string }) {
    // In CF-v1, we don't have canRunCheck option, so we'll just run the check
    return CHECKS.KYC.check();
  }

  // Updated use of connect config via params – to store. Revisit implementation later
  function connectToAgent() {
    // config?: ConnectToAgent | null,
    // selectionNames?: { category?: string; sectionName?: string }
    if (!connectConfig.value) {
      globalLogger.error('[CF] No connect config provided.');
      return false;
    }

    if (!canConnectToAgent(connectConfig.value) && !canCheckMetadataAutomation(connectConfig.value)) {
      globalLogger.error(
        '[CF] Incorrect connect to agent config provided. Cannot connect to agent with this config or check metadata.'
      );
      return false;
    }

    // const { category: selectionCategory, sectionName: selectionSection } = selectionNames || {};
    // const { category: configCategory, section: configSection } = config || {};

    // const category = selectionCategory || configCategory;
    // const section = selectionSection || configSection;

    // const transactionType = getActionKeyFromCategoryName(category || '');
    // const showTransactionSelector = connectConfig.value?.transactionTypes?.length || true;

    // if (showTransactionSelector) {
    //   conversation.addItems(
    //     createTransactionSelectMessage({
    //       transactionSubtypes:
    //         connectConfig.value?.transactionTypes?.map((type) => type as ExternalTransactionKind) || [],
    //       config: { connect: connectConfig.value },
    //     })
    //   );

    //   return;
    // }

    connectToSupportHook.trigger({
      connectToAgentConfig: connectConfig.value,
    });

    // chatBotConnectConfig: {
    //   connect: {
    //     showFollowup: false,
    //     showLiveChat: config.liveChat,
    //     showOpenRequest: config.openRequest,
    //     showVoiceChat: config.voiceChat,
    //   },
    //   loginRequired: config.loginRequired,
    //   selection: category + (section?.trim().length ? `+ ${section?.trim()}` : ''),
    //   type: 'wizard',
    //   // ...(transactionType ? { transaction: { type: transactionType } } : null),
    // },
    // TODO: add selection to connect config
    //   connectToAgentConfig: connectConfig.value,
    // });
    status.value = 'connect';
    return true;
  }

  function endConversationFlow(positiveResolution?: boolean) {
    // Show end chat.
    endChatHook.trigger({ positiveResolution });
    status.value = 'ended';
  }

  // function determineEndFlow({
  //   config,
  //   resolutionDraft,
  // }: {
  //   config: MessageConfiguration;
  //   resolutionDraft: BaseSystemMessage;
  // }) {
  //   if (config.resolution?.show) {
  //     conversation.addItems(resolutionDraft);
  //   } else if (canConnectToAgent(config.connect)) {
  //     connectToAgent(config.connect, {
  //       category: getCategoryTitleFromMessageHistory(resolutionDraft.history),
  //       sectionName: getSectionTitleFromMessageHistory(resolutionDraft.history),
  //     });
  //   } else {
  //     // endConversationFlow(true);
  //   }
  // }

  function generateArticleMessageHistory(event: ArticleInteractionExternEvent) {
    const sourceMessage = event.payload.sourceMessageId
      ? (conversation.messages.value.find((m) => m.id === event.payload.sourceMessageId) as BaseMessage) || null
      : null;

    const history = sourceMessage ? createMessageHistory(event, sourceMessage) : undefined;
    return history;
  }

  function addArticleUserMessage(option: ArticleSystemOption, event: BaseEvent | BaseExternalEvent) {
    const lastMessage = conversation.messages.value[conversation.messages.value.length - 1];
    const userSideArticleId = generateUserSideArticleMessageId(option.id);
    const isArticleMessageAlreadyExists = isMessageUserSideArticle(lastMessage?.id, option.id);

    const createMessageFactory =
      EventType.isArticleInteractionEvent(event) && !event.payload.sourceMessageId
        ? createUserSearchArticleMessage
        : createUserSystemMessage;

    if (!isArticleMessageAlreadyExists) {
      const userMessage = createMessageFactory({
        id: userSideArticleId,
        message: i18n.t('chat.user.response.message.text', { name: option.title }) as string,
        interactiveMessageParts: [i18n.t('chat.user.response.message.part') as string, option.title],
        history: EventType.isExternalEvent(event)
          ? generateArticleMessageHistory(event as ArticleInteractionExternEvent)
          : createMessageHistory(event, event.message),
      });

      // Looks like you have checked out our article, you can create the new
      conversation.addItems(userMessage); // { event, tags: ['from:visitor', 'opt.article'] }
    }
  }

  function updateMessageSelection(message: BaseMessage, selected: string | SystemOption) {
    const updatedMessage = { ...message, selectedOption: selected };
    conversation.addItems(updatedMessage);
  }

  // TODO: remove connect config from sending in params
  function connectArticleWithAcknowledgement({
    history,
    supportOptions,
  }: {
    supportOptions?: ConnectToAgent | null;
    history?: BaseMessage['history'];
  }) {
    const acknowledgement = createSystemMessage({
      type: MessageType.PRE_SUPPORT_ACKNOWLEDGEMENT,
      message: i18n.t('chat.v2.sys.lets_connect_to_agent') as string,
      history,
      config: { connect: supportOptions },
    });

    conversation.addItems(acknowledgement); // { event, tags: ['article.connect'] }

    connectToAgent();
    //   acknowledgement.config?.connect, {
    //   category: getCategoryTitleFromMessageHistory(history),
    //   sectionName: getSectionTitleFromMessageHistory(history),
    // }
  }

  function connectToAgentFromEvent(
    event: ArticleListNavigationEvent | PreSupportSelectionEvent | AlternativeActionSelectedEvent
  ) {
    const KNOWN_SELECTION_VALIDATION = [PredefinedOptionType.CONNECT_TO_SUPPORT, PredefinedOptionType.ISSUE_NOT_LISTED];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!KNOWN_SELECTION_VALIDATION.includes(event.payload.selected as any)) {
      globalLogger.warn('[CF] The connect to agent from event is coming from unrecognized event', { ...event });
    }

    const {
      message,
      payload: { selected },
    } = event;

    updateMessageSelection(message, selected);
    setConnectConfig({
      ...event.message.config?.connect!,
      category: getCategoryTitleFromMessageHistory(message.history) || '',
      section: getSectionTitleFromMessageHistory(message.history) || '',
    });

    connectArticleWithAcknowledgement({
      history: createMessageHistory(event, event.message),
      supportOptions: event.message.config?.connect,
    });
  }

  async function onArticleConnect(event: ArticleInteractionExternEvent) {
    const { supportOptions, article: sourceArticleOption } = event.payload;
    addArticleUserMessage(sourceArticleOption, event);

    const articleId = sourceArticleOption.articleId;

    if (supportOptions.enableMetadataRules && !user.value.isGuest && articleId != null) {
      await PleasedApi.getMetadataAutomations({ articleId })
        .then(({ data }) => {
          supportOptions.metadataRules = data;
        })
        .catch(noop);
    }

    const history = generateArticleMessageHistory(event);
    setConnectConfig(supportOptions);

    connectArticleWithAcknowledgement({ history, supportOptions });
  }

  // TODO: check usage, should be handled but in a bit other way
  function onCancelSupportRequest() {
    // const message = createUserSystemMessage({
    //   message: i18n.t('chat.user.issue.cancel_request') as string,
    // });

    // conversation.addItems(message); // { event, tags: ['cancel.support'] }
    const cancelRequestMessage = createSystemMessage({
      type: MessageType.CANCEL_REQUEST,
      message: i18n.t('chat.sys.msg.resolution.cancel_request') as string,
      config: { connect: connectConfig.value },
    });

    conversation.addItems(cancelRequestMessage);
    // status.value = 'ongoing';
    // const cancelSupportMessage = createAlternativeActionsMessage({ config: { connect: event.payload.supportOptions } });
    // conversation.addItems(cancelSupportMessage); //{ event, tags: ['cancel.support'] }
  }

  // TODO: support this
  function onArticleActionExecute(event: ArticleInteractionExternEvent) {
    globalLogger.error(
      '[CF] Action executed from article pages (external actions outside chat messages) are not handled by the CF'
    );
  }

  function positiveResolutionPreSupportPrompt(evt: PreSupportSelectionEvent) {
    updateMessageSelection(evt.message, PredefinedOptionType.POSITIVE_RESOLUTION);
    conversation.addItems(createUserSystemMessage({ message: i18n.t('chat.v2.user.my_issue_resolved') as string }));
    const message = createSystemMessage({
      type: MessageType.PRE_SUPPORT_PROMPT_BARE,
      message: i18n.t('chat.v2.sys.glad_to_help') as string,
      history: createMessageHistory(evt, evt.message),
    });
    conversation.addItems(message); // { event: evt, tags: ['from:system'] }
  }

  function onExploreOtherArticlesFromPreSupportPrompt(evt: PreSupportSelectionEvent) {
    updateMessageSelection(evt.message, PredefinedOptionType.BROWSE_OTHER_ARTICLES);
    const message = createSystemMessage({
      type: MessageType.PRE_SUPPORT_PROMPT_BARE,
      message: i18n.t('global.browse_other_topics') as string,
      history: createMessageHistory(evt, evt.message),
    });
    conversation.addItems(message); // { event: evt, tags: ['from:system'] }
  }

  function onArticleBackToChatroom(event: ArticleInteractionExternEvent) {
    // Can be an external event. Not coming from message.
    // There are 3 messages here to add.
    // 1. Intermediate resolution.
    // 2. user side article search.
    const { supportOptions, article: sourceArticleOption } = event.payload;
    addArticleUserMessage(sourceArticleOption, event);

    // Article has connect, but no action.

    const sourceMessage = event.payload.sourceMessageId
      ? (conversation.messages.value.find((m) => m.id === event.payload.sourceMessageId) as BaseMessage) || null
      : null;
    const history = sourceMessage ? createMessageHistory(event, sourceMessage) : undefined;

    const hasAction = Boolean(sourceArticleOption.action?.action);
    const hasSupport = canConnectToAgent(supportOptions);

    const connect: ConnectToAgent | undefined = supportOptions
      ? {
          ...supportOptions,
          category:
            supportOptions.category ||
            sourceArticleOption?.category ||
            (history ? getCategoryTitleFromMessageHistory(history) || '' : ''),
          section:
            supportOptions.section ||
            sourceArticleOption?.section ||
            (history ? getSectionTitleFromMessageHistory(history) || '' : ''),
        }
      : undefined;

    // Maybe set from article page and use
    setConnectConfig(connect || null);

    const nextMessage = switcher<PrePromptConditions, BaseSystemMessage>({ hasAction, hasSupport })
      .when(conditionHelper({ hasAction: true, hasSupport: true }), () =>
        createSystemMessage({
          type: MessageType.PRE_SUPPORT_PROMPT_ACTION_WITH_SUPPORT,
          message: i18n.t('chat.v2.sys.back_from_article') as string,
          config: {
            action: sourceArticleOption.action,
            connect,
          },
          history,
        })
      )
      .when(conditionHelper({ hasAction: false, hasSupport: false }), () =>
        createSystemMessage({
          type: MessageType.PRE_SUPPORT_PROMPT_BARE,
          message: i18n.t('chat.v2.sys.back_from_article') as string,
          history,
        })
      )
      .when(conditionHelper({ hasAction: false, hasSupport: true }), () =>
        createSystemMessage({
          type: MessageType.PRE_SUPPORT_PROMPT_SUPPORT_ONLY,
          message: i18n.t('chat.v2.sys.back_from_article') as string,
          config: {
            connect,
          },
          history,
        })
      )
      .when(conditionHelper({ hasAction: true, hasSupport: false }), () =>
        createSystemMessage({
          type: MessageType.PRE_SUPPORT_PROMPT_ACTION_ONLY,
          message: i18n.t('chat.v2.sys.back_from_article') as string,
          config: {
            action: sourceArticleOption.action,
          },
          history,
        })
      )
      .when(conditionHelper({ hasMetadataRules: true, hasSupport: false }), () =>
        createSystemMessage({
          type: MessageType.PRE_SUPPORT_PROMPT_METADATA_ONLY,
          message: i18n.t('chat.v2.sys.back_from_article') as string,
          config: {
            connect,
            action: sourceArticleOption.action,
          },
          history,
        })
      )
      .match();

    if (!nextMessage) {
      globalLogger.error('[CF] Not able to determine next message when back from the article page.');
    }

    // We want to safely replace the message.
    conversation.addItems({ ...nextMessage, id: `${nextMessage.type}--(articleID=${sourceArticleOption.id})` }); // { event, tags: ['from:system'] }
  }

  // function onNoneOfTheAboveSelected(event: NoneOfTheAboveEvent) {
  //   const {
  //     message: { config, id },
  //     message,
  //   } = event;
  //   // If there is no connect to agent/resolution config, we cannot show
  //   // anything. In such cases, We need to handle it in a better way.
  //   if (!(config && config.noneOfTheAbove && config.noneOfTheAbove?.config)) return;

  //   const { resolution } = config.noneOfTheAbove?.config || {};
  //   const { showResponseMessage = false, responseMessage = '' } = config.noneOfTheAbove || {};
  //   if (showResponseMessage && responseMessage) {
  //     conversation.addItems(
  //       createSystemMessage({
  //         type: MessageType.DEFAULT,
  //         id: `none-of-above-${randomId()}`,
  //         message: responseMessage,
  //       })
  //     );
  //   }

  //   determineEndFlow({
  //     config: config.noneOfTheAbove.config,
  //     resolutionDraft: createResolutionMessage({
  //       id: `${id}-none-of-the-above`,
  //       message: resolution?.message || (i18n.t('chat.sys.resolution.default') as string),
  //       history: createMessageHistory(event, message),
  //       config: config?.noneOfTheAbove?.config,
  //     }),
  //   });
  // }

  function connectToAgentFromIssueNotListed(event: ArticleListNavigationEvent) {
    const hardcodedConnectConfigParams = {
      showDescriptionBox: true,
      loginRequired: false,
      liveChat: true,
      openRequest: true,
      voiceChat: true,
    };

    const eventConnectConfig = event.message.config?.connect;

    const updatedEvent = {
      ...event,
      message: {
        ...event.message,
        config: {
          ...event.message.config,
          connect: { ...(eventConnectConfig || {}), ...hardcodedConnectConfigParams },
        },
      },
    };

    connectToAgentFromEvent(updatedEvent);
  }

  function getUserMessage(helpTopic: string, event?: BaseEvent) {
    return createUserSystemMessage({
      sendToChat: true,
      message: i18n.t('chat.user.response.message.text', { name: helpTopic }) as string,
      interactiveMessageParts: [i18n.t('chat.user.response.message.part', { name: helpTopic }) as string, helpTopic],
      ...(event ? { history: createMessageHistory(event, event.message) } : {}),
    });
  }

  function openSectionsFromArticleList(event: BaseEvent) {
    // Need to know the category.
    const { message: evtMessage } = event;
    const message = evtMessage as ArticleListWithOtherNavigationMessage;

    const sections = message.sections;
    const userResponse = getUserMessage(i18n.t('chat.v2.user.section.other_articles') as string, event);
    conversation.addItems(userResponse); // { event, tags: ['from:visitor'] }

    const updatedMessage = { ...message, selectedOption: PredefinedOptionType.BROWSE_OTHER_ARTICLES };

    conversation.addItems(updatedMessage);

    const sectionMessage = createSystemMessage<SystemListMessage>({
      type: MessageType.SECTION_LIST,
      options: sections,
      config: evtMessage.config,
      message: i18n.t('chat.v2.sys.section.more_sections_title') as string,
      history: createMessageHistory(event, message),
    });

    conversation.addItems(sectionMessage); // { event, tags: ['from:system'] }
  }

  async function navigateResolution(event: ResolutionEvent) {
    const { message } = event;
    const { selectedText } = event.payload;

    updateMessageSelection(message, event.payload.selected);

    if (selectedText) {
      conversation.addItems(
        createUserSystemMessage({
          message: selectedText,
        })
      );
    }

    if (ItemType.isLandingAiVoiceConfirmationMessage(message)) {
      await handleLandingAiVoiceConfirmationResolution(event);
      return;
    }

    switcher<ResolutionEvent>(event)
      .when((evt) => ItemType.isMetadataResolutionMessage(evt.message), handleMetadataResolution)
      .when((evt) => ItemType.isAnyOtherHelpResolutionMessage(evt.message), handleAnyOtherHelpResolution)
      .when((evt) => ItemType.isVoiceConfirmationMessage(evt.message), handleVoiceConfirmationResolution)
      .when(
        (evt) => ItemType.isVoiceNativeMicroAccessConfirmationMessage(evt.message),
        handleVoiceNativeMicroAccessConfirmationResolution
      )
      .when((evt) => ItemType.isCancelRequestMessage(evt.message), handleCancelRequestResolution)
      .when((evt) => ItemType.isEndLiveChatMessage(evt.message), handleEndLiveChatResolution)
      .when((evt) => ItemType.isEndVoiceCallMessage(evt.message), handleEndVoiceCallResolution)
      .when((evt) => ItemType.isRestartChatResolutionMessage(evt.message), handleRestartChatResolution)
      .none(() => globalLogger.error('[CF] Unhandled resolution event', { ...event }))
      .match();
  }

  function handleMetadataResolution(event: ResolutionEvent) {
    const { message, payload } = event;
    const wasHelpful = payload.selected === ResolutionType.YES;

    if (wasHelpful) {
      logger.track({
        type: EVENT_TYPES.FEATURE,
        name: FEATURES_EVENTS.METADATA_AUTOMATION_HELPFUL,
        selection: message.config?.connect?.appliedMetadataAutomation?.id,
      });

      const anyOtherHelpMessage = createSystemMessage({
        type: MessageType.ANY_OTHER_HELP_RESOLUTION,
        message: i18n.t('chat.sys.msg.resolution.help_anything_else') as string,
      });
      conversation.addItems(anyOtherHelpMessage);
    } else {
      const canConnect = canConnectToAgent(message.config?.connect);

      logger.track({
        type: EVENT_TYPES.FEATURE,
        name: FEATURES_EVENTS.METADATA_AUTOMATION_NOT_HELPFUL,
        selection: message.config?.connect?.appliedMetadataAutomation?.id,
        metadata: { canConnect },
      });

      const failedResolutionMessage = createSystemMessage({
        type: canConnect
          ? MessageType.PRE_SUPPORT_PROMPT_FAILED_RESOLUTION_WITH_SUPPORT
          : MessageType.PRE_SUPPORT_PROMPT_FAILED_RESOLUTION,
        message: i18n.t('chat.sys.msg.resolution.failed_resolution') as string,
        config: { connect: message.config?.connect },
      });
      conversation.addItems(failedResolutionMessage);
    }
  }

  function handleAnyOtherHelpResolution(event: ResolutionEvent) {
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      restartChat();
    } else if (selected === ResolutionType.NO) {
      endConversationFlow();
    }
  }

  async function handleLandingAiVoiceConfirmationResolution(event: ResolutionEvent) {
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      const isAvailable = await checkVoiceAiAvailabilityOrShowModal();
      if (!isAvailable) {
        return;
      }

      startLandingAiVoiceCall();
      return;
    }

    if (selected === ResolutionType.NO) {
      resetLandingToDefaultState();
    }
  }

  function handleVoiceConfirmationResolution(event: ResolutionEvent) {
    const { selected } = event.payload;

    if (selected === ResolutionType.NO) {
      devLog.debug('[FLOW_EVENT_HANDLERS] Voice call cancelled by user');
      onCancelSupportRequest();
      return;
    }

    if (selected !== ResolutionType.YES) return;

    if (!getShouldUseNativeMicroAccessCheck()) {
      connectOptionSelectedHook.trigger({ connectOption: ConnectOptionType.VOICE_CALL });
      return;
    }

    // For native app, we need to check if the user has granted microphone access and request it if not.
    requestNativeAppVoiceMicrophonePermission().then((allowed) => {
      if (!allowed) {
        sendNativeAppVoiceMicrophoneSettings();

        const nativeMicroFollowUpMessage = createSystemMessage({
          type: MessageType.VOICE_NATIVE_MICRO_ACCESS_CONFIRMATION,
          message: i18n.t('page.voice.native_micro_access_follow_up_app') as string,
        });

        conversation.addItems(nativeMicroFollowUpMessage);

        return;
      }

      connectOptionSelectedHook.trigger({ connectOption: ConnectOptionType.VOICE_CALL });
    });
  }

  function handleVoiceNativeMicroAccessConfirmationResolution(event: ResolutionEvent) {
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      connectOptionSelectedHook.trigger({ connectOption: ConnectOptionType.VOICE_CALL });
      return;
    }

    if (selected === ResolutionType.NO) {
      devLog.debug('[FLOW_EVENT_HANDLERS] Voice call cancelled by user after native microphone step');
      onCancelSupportRequest();
    }
  }

  function handleCancelRequestResolution(event: ResolutionEvent) {
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      // User wants to contact support
      devLog.debug('[FLOW_EVENT_HANDLERS] User selected to contact support');
      abortCancelRequestHook.trigger({});
    } else if (selected === ResolutionType.NO) {
      // User wants to end chat
      devLog.debug('[FLOW_EVENT_HANDLERS] User selected to end chat');
      endConversationFlow();
    }
  }

  function handleEndLiveChatResolution(event: ResolutionEvent) {
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      // User confirmed to end live chat
      devLog.debug('[FLOW_EVENT_HANDLERS] User confirmed to end live chat');
    } else if (selected === ResolutionType.NO) {
      // User cancelled - remove the resolution message
      devLog.debug('[FLOW_EVENT_HANDLERS] User cancelled ending live chat');
      // conversation.deleteItem(event.message);
    }

    endLiveChatHook.trigger({ resolution: selected });
  }

  function handleEndVoiceCallResolution(event: ResolutionEvent) {
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      // User confirmed to end voice call
      devLog.debug('[FLOW_EVENT_HANDLERS] User confirmed to end voice call');
    } else if (selected === ResolutionType.NO) {
      // User cancelled - remove the resolution message
      devLog.debug('[FLOW_EVENT_HANDLERS] User cancelled voice call');
      // conversation.deleteItem(event.message);
    }

    endVoiceCallHook.trigger({ resolution: selected });
  }

  function handleRestartChatResolution(event: ResolutionEvent) {
    const { selected } = event.payload;

    if (selected === ResolutionType.YES) {
      restartChat();
    } else if (selected === ResolutionType.NO) {
      // Nothing to do here, user cancelled the restart chat
    }
  }

  function onRollback(event: RollbackEvent) {
    conversation.deleteAllMessagesFrom(event.message);
  }

  function navigateAlternativeAction(event: AlternativeActionSelectedEvent) {
    const {
      payload: { selected },
    } = event;
    if (selected === PredefinedOptionType.RESTART_CHAT) return restartChat();
    if (selected === PredefinedOptionType.END_CHAT) return endConversationFlow();
    if (selected === PredefinedOptionType.CONNECT_TO_SUPPORT) return connectToAgentFromEvent(event);
    // TODO: support later
    // if (selected === PredefinedOptionType.GO_TO_TICKET) return this.emitter.emit('goToTicket', config?.directRequest);
  }

  async function handleTransactionSelected(event: UserSelectedTransactionListEvent) {
    const { transactionType, transactions } = event.payload;

    const transactionTypeName = TRANSACTION_TYPE_NAME_ENUM[transactionType];
    const isTransactionSelected = transactions.length > 0;
    const selectedTransaction = transactions[0] as ExtendedSelectedTransStatement;

    const displayTransactionType = transactions.length > 1 ? `${transactionTypeName}(s)` : transactionTypeName;
    const transactionIds = transactions.map((tx) => getTransactionId(tx)).join(', ');

    // Create a selected transaction message
    if (isTransactionSelected) {
      await conversation.enqueue(
        combineTriggerCheck({
          into: Promise.resolve(
            createSelectedTransactionMessage({
              transactions,
              transactionType,
              message: i18n.t('chat.sys.msg.ai.transaction_help', {
                transactionType: displayTransactionType,
                transactionId: transactionIds,
                userEvents: '',
              }) as string,
              isEditable: true,
            })
          ),
          type: 'TRANSACTION',
          transactionType: transactionTypeName,
          transaction: selectedTransaction,
        })
      );
    } else {
      conversation.addItems(
        createSelectedTransactionMessage({
          transactions: [],
          transactionType,
          message: i18n.t('chat.sys.msg.ai.transaction_not_listed_help') as string,
          isEditable: true,
        })
      );
    }

    transactionSelectedHook.trigger({ transactionType, transactions });
  }

  function handleEditSelectedTransaction(event: UserEditSelectedTransactionEvent) {
    const { message } = event.payload;
    if (message) {
      const updatedMessage = { ...message, wasEdited: true };
      conversation.addItems(updatedMessage as BaseMessage);
    }
    editSelectedTransactionHook.trigger();
  }

  /**
   * Collapsed landing entry bar on new-home: `InteractionView.LANDING` (readonly ChatInput, overlay closed).
   * Use with `showLandingChannelPicker()` when opening the overlay from this state.
   */
  function enterLandingCollapsedState() {
    if (!isLandingEntrySupported({ userId: getLandingEntryUserId() })) {
      return;
    }

    setInteractionView(InteractionView.LANDING);
  }

  /**
   * Open landing channel picker in the overlay: `InteractionView.NONE` plus a pending
   * `ConnectOptionSelectMessage`. After `restartConversation()`, callers that hide the overlay
   * must call `enterLandingCollapsedState()` to return to the collapsed entry bar.
   */
  function showLandingChannelPicker() {
    if (!isLandingEntrySupported({ userId: getLandingEntryUserId() })) {
      return;
    }

    const hasPendingLandingChannels = conversation.messages.value.some(
      (message) => ItemType.isConnectOptionSelectMessage(message) && message.isLandingEntry && !message.selectedOption
    );

    if (hasPendingLandingChannels) {
      return;
    }

    setInteractionView(InteractionView.NONE);

    conversation.addItems(
      createConnectOptionSelectMessage({
        id: 'landing-channel-select',
        isLandingEntry: true,
        message: i18n.t('page.connect.landing.greeting') as string,
        showLiveChat: true,
        showVoiceChat: true,
        // Temporarily turn off "Open Request" as it has been postponed at this step of implementation.
        showOpenRequest: false,
      })
    );
  }

  async function enterDefaultCategoryBrowsing(options?: { id?: string; history?: SystemListMessage['history'] }) {
    const brandConfig = await useBrandConfig().fetchBrandConfig();
    const categoryMessage = buildCategoryListMessageFromBrandConfig(brandConfig, options);

    conversation.addItems(categoryMessage);
    setConnectConfig(getAiHardcodedConnectConfig());
    setInteractionView(aiHandlers.checkCanConnectToAI() ? InteractionView.INPUT : InteractionView.NONE);
  }

  async function enterLiveChatFromLanding() {
    await enterDefaultCategoryBrowsing();
  }

  function enterOpenRequestFromLanding() {
    connectToSupportHook.trigger({
      connectToAgentConfig: {
        connect: true,
        landingOpenRequest: true,
        loginRequired: false,
        liveChat: false,
        voiceChat: false,
        openRequest: true,
        showDescriptionBox: false,
      },
    });
  }

  const showVoiceAiUnavailableModal = () => {
    voiceAiUnavailableHook.trigger();
  };

  const checkVoiceAiAvailabilityOrShowModal = async (): Promise<boolean> => {
    const availability = await PleasedApi.getVoiceAiAvailability().catch(() => ({ available: false }));

    if (!availability.available) {
      queryClient.setQueryData(VOICE_AI_AVAILABILITY_QUERY_KEY, availability);
      showVoiceAiUnavailableModal();
      return false;
    }

    return true;
  };

  function enterVoiceCallFromLanding() {
    conversation.addItems(
      createSystemMessage({
        type: MessageType.LANDING_AI_VOICE_CONFIRMATION,
        message: i18n.t('page.voice.disclaimer') as string,
      })
    );
  }

  function startLandingAiVoiceCall() {
    setInteractionView(InteractionView.LANDING_AI_VOICE_INDICATOR);
    landingAiVoiceStartHook.trigger();
  }

  function resetLandingToDefaultState() {
    conversation.clear({ __CLEARING_CONVERSATION: true });

    if (!enterSkipToLiveAgentFlow()) {
      showLandingChannelPicker();
    }
  }

  function enterSkipToLiveAgentFlow(): boolean {
    // Brand config is loaded once in startFlow; reuse the cached store value.
    if (!brandConfig.value || !isSkipToLiveAgentActive.value) {
      return false;
    }

    const skipWelcomeMessage = buildSkipWelcomeSystemMessage(brandConfig.value);

    if (skipWelcomeMessage && !hasSkipWelcomeMessage(conversation.messages.value)) {
      conversation.addItems(
        createSystemMessage({
          id: skipWelcomeMessage.id,
          type: MessageType.DEFAULT,
          message: skipWelcomeMessage.message,
        })
      );
    }

    const connectToAgentConfig = buildSkipToLiveAgentConnectConfig(brandConfig.value);
    setConnectConfig(connectToAgentConfig);
    connectToSupportHook.trigger({ connectToAgentConfig });
    return true;
  }

  function recordConnectOptionSelection(event: UserSelectedConnectOptionEvent) {
    const { connectOption, message } = event.payload;
    message && updateMessageSelection(message, connectOption);

    const channelName = getConnectOptionChannelLabel(connectOption);
    conversation.addItems(
      createUserSystemMessage({
        type: MessageType.DEFAULT,
        message: channelName,
      })
    );
  }

  async function handleLandingConnectOptionSelected(event: UserSelectedConnectOptionEvent) {
    const { connectOption } = event.payload;

    recordConnectOptionSelection(event);

    if (connectOption === ConnectOptionType.LIVE_CHAT) {
      await enterLiveChatFromLanding();
      return;
    }

    if (connectOption === ConnectOptionType.VOICE_AI) {
      const isAvailable = await checkVoiceAiAvailabilityOrShowModal();
      if (!isAvailable) {
        return;
      }

      enterVoiceCallFromLanding();
      return;
    }

    if (connectOption === ConnectOptionType.OPEN_REQUEST) {
      enterOpenRequestFromLanding();
    }
  }

  function handleConnectOptionSelected(event: UserSelectedConnectOptionEvent) {
    if (event.payload.message?.isLandingEntry) {
      return handleLandingConnectOptionSelected(event);
    }

    // TODO: Implement logic for handling selected connect option
    // This will handle LIVE_CHAT, VOICE_CALL, OPEN_REQUEST, and FOLLOW_UP options
    recordConnectOptionSelection(event);

    switcher<ConnectOptionKind>(event.payload.connectOption)
      .when([ConnectOptionType.LIVE_CHAT, ConnectOptionType.FOLLOW_UP], () => {
        devLog.debug('[FLOW_EVENT_HANDLERS] Live chat or follow up selected');
        // Execute connect to agent flow directly, as not additional confirmation is needed
        connectOptionSelectedHook.trigger(event.payload);
      })
      .when(ConnectOptionType.VOICE_CALL, () => {
        devLog.debug('[FLOW_EVENT_HANDLERS] Voice call selected');
        // Show voice confirmation resolution message
        const voiceConfirmationMessage = createSystemMessage({
          type: MessageType.VOICE_CONFIRMATION,
          message: i18n.t('page.voice.disclaimer') as string,
        });

        conversation.addItems(voiceConfirmationMessage);
        connectOptionSelectedHook.trigger({ connectOption: ConnectOptionType.VOICE_CALL, confirmation: true });
      })
      .when(ConnectOptionType.OPEN_REQUEST, () => {
        devLog.debug('[FLOW_EVENT_HANDLERS] Open request selected');
        const openRequestPromptMessage = createSystemMessage({
          type: MessageType.DEFAULT,
          message: i18n.t('page.connect.open_request.describe_problem_prompt') as string,
        });
        conversation.addItems(openRequestPromptMessage);
        connectOptionSelectedHook.trigger({ connectOption: ConnectOptionType.OPEN_REQUEST, confirmation: true });
      })
      .when(ConnectOptionType.CANCEL, () => {
        devLog.debug('[FLOW_EVENT_HANDLERS] Cancel selected');
        connectOptionSelectedHook.trigger({ connectOption: ConnectOptionType.CANCEL });
        submitExternalAction({ type: ExternalBotEvent.CANCEL_SUPPORT_REQUEST, id: randomId() });
      })
      .match();
  }

  async function resolvePredictiveSelection(event: PredictiveSuggestionSelectionEvent) {
    const {
      message,
      payload: { selected },
    } = event;

    // Update message selection
    updateMessageSelection(message, selected);
    const predictionId = message.config?.matchedPredictiveOption!;

    if (selected === PredefinedOptionType.NO_OTHER_ISSUE) {
      // Add user message indicating no other issue
      const userMessage = createUserSystemMessage({
        message: i18n.t('chat.user.response.message.text', { name: i18n.t('global.no') }) as string,
        interactiveMessageParts: [i18n.t('chat.user.response.message.part') as string, i18n.t('global.no') as string],
        history: createMessageHistory(event, message),
      });
      conversation.addItems(userMessage);

      const brandConfigData = await useBrandConfig().fetchBrandConfig();
      const categoryMessage = buildCategoryListMessageFromBrandConfig(brandConfigData, {
        id: `category-from-prediction-${randomId()}`,
        history: createMessageHistory(event, message),
      });
      conversation.addItems(categoryMessage);
      return;
    }

    // Handle CONNECT_TO_SUPPORT
    const uiMessage = getPredictionById(predictionId)?.ui() || '';
    const userMessage = createUserSystemMessage({
      message: i18n.t('chat.user.response.message.text', { name: uiMessage }) as string,
      interactiveMessageParts: [i18n.t('chat.user.response.message.part') as string, uiMessage],
      history: createMessageHistory(event, message),
    });
    conversation.addItems(userMessage);

    // Check if there is article navigation
    const articleId = message.config?.connect?.redirectToArticleId;
    if (articleId) {
      // Navigate to article
      const { data: article } = await PleasedApi.getArticleDetails.cached({ articleId });

      if (article) {
        const articleOption = buildArticleOption({
          ...article.articleConfig,
          id: article.id,
          title: article.title,
          category: article.category?.titleTranslated || article.category?.title || '',
        });
        moveUserToArticlePage(articleOption, message.id as string);
      }

      return;
    }

    // No article navigation, connect to agent
    aiHandlers.aiFlow.value.enabledPerSession = false;
    setConnectConfig(message.config?.connect || null);
    connectArticleWithAcknowledgement({
      history: createMessageHistory(event, message),
      supportOptions: message.config?.connect,
    });
  }

  function onEndLiveChatRequest() {
    const resolutionMessage = createSystemMessage({
      type: MessageType.END_LIVE_CHAT,
      message: i18n.t('chat.sys.msg.resolution.end_live_chat') as string,
    });
    conversation.addItems(resolutionMessage);
  }

  function onEndVoiceCallRequest() {
    const resolutionMessage = createSystemMessage({
      type: MessageType.END_VOICE_CALL,
      message: i18n.t('chat.sys.msg.resolution.end_voice_call') as string,
    });
    conversation.addItems(resolutionMessage);
  }

  function onRestartChatResolutionRequest() {
    const resolutionMessage = createSystemMessage({
      type: MessageType.RESTART_CHAT_RESOLUTION,
      message: i18n.t('chat.sys.msg.resolution.restart_chat') as string,
    });
    conversation.addItems(resolutionMessage);
  }

  function onResumeLiveChatRequest(event: ResumeLiveChatExternEvent) {
    const { payload } = event;
    status.value = 'ongoing';
    resumeChatHook.trigger(payload);
  }

  // function onLowCsatConnect(_event: LowCsatConnectExternEvent) {
  //   // Disable AI flow
  //   aiHandlers.aiFlow.value.enabledPerSession = false;

  //   // Set the connect config in store
  //   setConnectConfig(getAiHardcodedConnectConfig());
  //   connectToAgent();
  // }

  function onApplyMetadataAutomation(event: ApplyMetadataAutomationExternEvent) {
    const {
      payload: { metadataAutomationMessage, metadataAutomationRule, transaction, transactionType },
    } = event;

    const supportOptions: ConnectToAgent | null = event.payload.supportOptions
      ? {
          ...event.payload.supportOptions,
          appliedMetadataAutomation: metadataAutomationRule,
          metadataAutomationLogTraceId: crypto.randomUUID(),
        }
      : null;

    const showMetadataMessage = Boolean(metadataAutomationRule && metadataAutomationMessage);

    if (!showMetadataMessage) {
      // If no rule found, then continue default flow
      conversation.addItems(
        createSelectedTransactionMessage({
          transactions: [transaction],
          transactionType: transactionType!,
          isEditable: true,
        })
      );

      transactionSelectedHook.trigger({ transactionType: transactionType!, transactions: [transaction] });

      const hasAction = Boolean(supportOptions?.action?.action);

      if (!supportOptions || !canConnectToAgent(supportOptions)) {
        const bareMessage = createSystemMessage({
          type: hasAction ? MessageType.PRE_SUPPORT_PROMPT_ACTION_ONLY : MessageType.PRE_SUPPORT_PROMPT_BARE,
          message: i18n.t('chat.v2.sys.back_from_article') as string,
        });
        conversation.addItems(bareMessage);
      }

      return;
    }

    // Reset status as we're not connecting to agent anymore
    status.value = 'ongoing';

    const responseMessage = createSystemMessage({
      type: MessageType.DEFAULT,
      message: metadataAutomationMessage,
    });

    const resolutionMessage = createSystemMessage({
      type: MessageType.METADATA_RESOLUTION,
      message: i18n.t('chat.sys.msg.resolution.metadata_resolution') as string,
      config: { connect: supportOptions },
    });

    conversation.addItems(responseMessage, resolutionMessage);
  }

  function onSearchArticles(_event: PreSupportSelectionEvent) {
    router.push('/new-help');
    setIsChatOverlayVisible(false);
  }

  const eventSwitch = switcher()
    .when(EventType.isOptionEvent, (event) => {
      if (isCategoryOption(event.payload.selected)) return onCategorySelected(event);
      if (isSectionOption(event.payload.selected)) return onSectionSelected(event);
      if (isArticleOption(event.payload.selected)) return onArticleSelected(event);
    })
    .when(EventType.isArticleListNavigationEvent, (event) => {
      const selected = event.payload.selected;
      if (isArticleOption(selected)) return onArticleSelected(event);
      if (selected === PredefinedOptionType.BROWSE_OTHER_ARTICLES) return openSectionsFromArticleList(event);
      // Directly connect through messages
      if (selected === PredefinedOptionType.ISSUE_NOT_LISTED) connectToAgentFromIssueNotListed(event);
    })
    // Outdated not used
    // .when(EventType.isArticleExpandedEvent, onArticleExpanded)
    // .when(EventType.isNoneOfTheAboveEvent, onNoneOfTheAboveSelected)
    .when(EventType.isResolutionEvent, navigateResolution)
    .when(EventType.isRollbackEvent, onRollback)
    .when(EventType.isPreSupportPromptSelectionEvent, (evt) => {
      const selected = evt.payload.selected;

      if (selected === PredefinedOptionType.CONNECT_TO_SUPPORT) return connectToAgentFromEvent(evt);
      if (selected === PredefinedOptionType.BROWSE_OTHER_ARTICLES)
        return onExploreOtherArticlesFromPreSupportPrompt(evt);
      if (selected === PredefinedOptionType.RESTART_CHAT) return restartChat();
      if (selected === PredefinedOptionType.END_CHAT) return endConversationFlow();
      if (selected === PredefinedOptionType.POSITIVE_RESOLUTION) return positiveResolutionPreSupportPrompt(evt);
      if (selected === PredefinedOptionType.NEED_MORE_HELP) return connectToAgentFromEvent(evt);
      if (selected === PredefinedOptionType.SEARCH_ARTICLES) return onSearchArticles(evt);
    })
    .when(EventType.isAlternativeActionEvent, navigateAlternativeAction)
    .when(EventType.isUserSelectedTransactionListEvent, handleTransactionSelected)
    .when(EventType.isPredictiveSuggestionSelectionEvent, resolvePredictiveSelection)
    .none(() => globalLogger.error('[CF] Event was not matched with any matcher'));

  const externEventSwitch = switcher<BaseExternalEvent>()
    .when(EventType.isArticleInteractionEvent, (evt) => {
      // Article page actions (Back, Contact support, etc.) are emitted from NewArticle via
      // submitExternalAction, not submitAction. Without this guard, those handlers would
      // resume normal article/browse flows (e.g. onArticleConnect with article-scoped connect
      // config). Skip mode must always route the user back to the live-agent connect wizard.
      if (redirectBrowseToSkipFlowIfActive()) {
        return;
      }

      // if (evt.payload.interaction === ArticleInteractionType.BACK) return onArticleBackToChatroom(evt);
      if (evt.payload.interaction === ArticleInteractionType.SUPPORT_REQUEST) return onArticleConnect(evt);
      if (evt.payload.interaction === ArticleInteractionType.EXECUTE_ACTION) return onArticleActionExecute(evt);
    })
    .when(EventType.isCancelSupportRequestEvent, onCancelSupportRequest)
    .when(EventType.isApplyMetadataAutomationEvent, onApplyMetadataAutomation)
    .when(EventType.isRestartChatEvent, restartChat)
    .when(EventType.isRestartChatResolutionEvent, onRestartChatResolutionRequest)
    .when(EventType.isUserEditSelectedTransactionEvent, handleEditSelectedTransaction)
    .when(EventType.isUserSelectedConnectOptionEvent, handleConnectOptionSelected)
    .when(EventType.isEndLiveChatEvent, onEndLiveChatRequest)
    .when(EventType.isEndVoiceCallEvent, onEndVoiceCallRequest)
    .when(EventType.isResumeLiveChatEvent, onResumeLiveChatRequest)
    // .when(EventType.isLowCsatConnectEvent, onLowCsatConnect)
    .none(() => globalLogger.error('[CF] Unhandled external event passed'));

  /**
   * Report handler failures with the event trail that led to them, so a flow that breaks
   * mid-session is not silent in GlitchTip (start-only capture misses everything after start).
   */
  async function dispatchFlowEvent<T>(evt: BaseEvent | BaseExternalEvent, dispatch: () => T | Promise<T>) {
    addBreadcrumb({
      category: 'chatbot.event',
      message: evt.type,
      level: 'info',
      data: { type: evt.type },
    });

    try {
      return await dispatch();
    } catch (error) {
      captureEventException(error, ErrorTrackingEvent.ChatbotFlowAction);
      throw error;
    }
  }

  async function submitAction(evt: BaseEvent) {
    // If the flow has been completed to end chat or connect request,
    // then any more actions can be safely ignored, except for related to connect flow (txs etc)
    const CONNECT_TO_SUPPORT_FLOW_EVENTS = [
      EventType.isUserSelectedTransactionListEvent,
      EventType.isUserSelectedConnectOptionEvent,
      EventType.isUserEditSelectedTransactionEvent,
      EventType.isResolutionEvent,
    ];

    const ACTIVE_AI_FLOW_EVENTS = [EventType.isUserSelectedTransactionListEvent, EventType.isResolutionEvent];

    if (aiHandlers.aiFlow.value.active && !ACTIVE_AI_FLOW_EVENTS.some((check) => check(evt))) {
      return;
    }

    const isEndedStatus = status.value === 'ended';
    const isConnectStatus = status.value === 'connect';
    const isConnectEventAllowed = CONNECT_TO_SUPPORT_FLOW_EVENTS.some((check) => check(evt));
    if (isEndedStatus || (isConnectStatus && !isConnectEventAllowed)) return;

    return dispatchFlowEvent(evt, () => {
      // First check if event should be handled by AI service separately / AI only related event.
      if (aiHandlers.testAiEventHandler(evt)) {
        return aiHandlers.submitAiEvent(evt);
      }

      // Then check if we should connect to AI instead of common event handler.
      if (aiHandlers.testAiConnectEventHandler(evt)) {
        return aiHandlers.submitAiConnectEvent(evt);
      }

      return eventSwitch.match(evt);
    });
  }

  async function submitExternalAction(evt: BaseExternalEvent) {
    return dispatchFlowEvent(evt, () => {
      // First check if event should be handled by AI service separately / AI only related event.
      if (aiHandlers.testAiEventHandler(evt)) {
        return aiHandlers.submitAiEvent(evt);
      }

      // Then check if we should connect to AI instead of common external event handler.
      if (aiHandlers.testAiConnectExternalEventHandler(evt)) {
        return aiHandlers.submitAiConnectExternalEvent(evt);
      }

      return externEventSwitch.match(evt);
    });
  }

  function resetFlow() {
    status.value = 'ongoing';
    aiHandlers.restartAiFlow();
    conversation.clear({ __CLEARING_CONVERSATION: true });
  }

  // Handler which will be triggered on restart, resets the session before calling
  // other functions, especially resetFlow with reset of default + AI Flows
  function restartChat() {
    logger.restartUserSession({
      sessionParams: {
        language: Locale.i18n.locale,
        platform: config?.platform || platform,
        userAgent: navigator.userAgent,
      },
      metadata: {
        brandCode: config?.brandCode,
        language: Locale.i18n.locale,
      },
    });

    restartChatHook.trigger({});
  }

  aiHandlers.onIntervention((data) => {
    status.value = 'connect';
  });

  return {
    submitAction,
    submitExternalAction,
    combineTriggerCheck,

    onConnectSupport: connectToSupportHook.on,
    onEndChat: endChatHook.on,
    onRestartChat: restartChatHook.on,
    onAbortCancelRequest: abortCancelRequestHook.on,

    onTransactionSelected: transactionSelectedHook.on,
    onEditSelectedTransaction: editSelectedTransactionHook.on,
    onConnectOptionSelected: connectOptionSelectedHook.on,

    onEndLiveChat: endLiveChatHook.on,
    onEndVoiceCall: endVoiceCallHook.on,
    onResumeChat: resumeChatHook.on,
    onVoiceAiUnavailable: voiceAiUnavailableHook.on,

    flowStatus: readonly(status),

    aiFlow: aiHandlers.aiFlow,
    submitUserMessageForAI: aiHandlers.submitUserMessageForAI,
    checkCanConnectToAI: aiHandlers.checkCanConnectToAI,
    onAiIntervention: aiHandlers.onIntervention,
    onAiFlowStart: aiHandlers.onAiFlowStart,
    onEndAiChat: aiHandlers.onEndAiChat,

    enterLandingCollapsedState,
    showLandingChannelPicker,
    enterSkipToLiveAgentFlow,
    startLandingAiVoiceCall,
    onLandingAiVoiceStart: landingAiVoiceStartHook.on,

    resetFlow,
  };
}
